/** @jsxImportSource preact */
import { h, Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { IshikawaBone } from '../lib/ishikawa';
import { createResizeObserver } from '../lib/responsive';

interface IshikawaDiagramProps {
  bones: IshikawaBone[];
  /** Incident title, drawn in the head box at the end of the spine. */
  title?: string;
}

// Below this container width the fishbone is drawn too small to read, so the
// component switches to the stacked list instead of shrinking text to 4px.
const FISHBONE_MIN_WIDTH = 640;

const PAD_X = 16;
const PAD_Y = 10;
const LABEL_H = 24;
const LABEL_GAP = 12;
const TICK_LEN = 16;
const TEXT_GAP = 7;
const CAUSE_FONT = 11.5;
const EXPL_FONT = 11;
const CAUSE_LINE = 14;
const EXPL_LINE = 13;
const BLOCK_GAP = 15;
const RUN_PAD = 14;
const SPINE_W = 2.5;

/**
 * Greedy word wrap to a character budget, hard-splitting any single word
 * longer than the budget. Same approach as the Python renderer's
 * `split_string_to_list`: SVG has no text metrics before paint, so width is
 * approximated from the character count rather than measured.
 */
function wrap(text: string, maxChars: number): string[] {
  if (!text) return [];
  const lines: string[] = [];
  let current = '';

  for (const word of text.split(/\s+/)) {
    if (!word) continue;
    if (!current) current = word;
    else if (current.length + word.length + 1 <= maxChars) current += ` ${word}`;
    else {
      lines.push(current);
      current = word;
    }
    while (current.length > maxChars) {
      lines.push(current.slice(0, maxChars));
      current = current.slice(maxChars);
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** How many characters of `fontSize` text fit in `maxWidth` px. */
function charBudget(maxWidth: number, fontSize: number, ratio = 0.53): number {
  return Math.max(10, Math.floor(maxWidth / (fontSize * ratio)));
}

interface LaidOutCause {
  causeLines: string[];
  explLines: string[];
  height: number;
}

interface LaidOutBone {
  category: string;
  colour: string;
  causes: LaidOutCause[];
  /** Vertical extent this bone's causes need. */
  run: number;
  /** x where the bone meets the spine. */
  xJoin: number;
  /** x of the bone's outer tip, under the category label. */
  xTip: number;
  labelWidth: number;
  labelFont: number;
}

interface SideLayout {
  bones: LaidOutBone[];
  run: number;
}

/** Lay out the bones on one side of the spine. */
function layoutSide(bones: IshikawaBone[], spineLen: number): SideLayout {
  if (bones.length === 0) return { bones: [], run: 0 };

  // Columns are always a third of the spine — the schema fixes three
  // categories per side — and a side with fewer bones centres them rather
  // than stretching each column over the whole spine, which would otherwise
  // shove a lone bone up against the arrowhead and let its text run the
  // full width.
  const pitch = spineLen / 3;
  const offset = (spineLen - bones.length * pitch) / 2;
  const slant = Math.min(90, Math.round(pitch * 0.28));
  // Text is right-aligned back from each tick, so capping it at
  // (pitch - slant) is exactly what keeps one bone's text out of the
  // neighbouring column.
  const textMaxWidth = Math.max(90, pitch - slant - 12);
  const causeChars = charBudget(textMaxWidth, CAUSE_FONT, 0.55);
  const explChars = charBudget(textMaxWidth, EXPL_FONT);

  const laid: LaidOutBone[] = bones.map((bone, i) => {
    const causes: LaidOutCause[] = bone.causes.map((c) => {
      const causeLines = wrap(c.cause, causeChars);
      const explLines = wrap(c.explanation, explChars);
      return {
        causeLines,
        explLines,
        height: causeLines.length * CAUSE_LINE + explLines.length * EXPL_LINE,
      };
    });

    const run =
      causes.reduce((sum, c) => sum + c.height, 0) +
      Math.max(0, causes.length - 1) * BLOCK_GAP +
      RUN_PAD * 2;

    const xJoin = PAD_X + offset + (i + 1) * pitch;
    // Long category names ("Regulatory Environment") must still fit their
    // column, so the label shrinks rather than overflowing into the next one.
    const labelFont = Math.max(9, Math.min(12, (pitch - 20) / (bone.category.length * 0.56)));
    const labelWidth = Math.min(pitch - 6, bone.category.length * labelFont * 0.56 + 18);

    return {
      category: bone.category,
      colour: bone.colour,
      causes,
      run,
      xJoin,
      xTip: xJoin - slant,
      labelWidth,
      labelFont,
    };
  });

  return { bones: laid, run: Math.max(...laid.map((b) => b.run)) };
}

/** Cause text blocks for one bone, positioned along its diagonal. */
function BoneCauses({ bone, sideRun, yTip, ySpine }: {
  bone: LaidOutBone;
  sideRun: number;
  yTip: number;
  ySpine: number;
}) {
  // Both halves fill downwards, so causes read in payload order whichever
  // side of the spine they are on, and in the same order as the list used on
  // narrow containers. (The Python renderer numbered outwards from the spine
  // on the lower half, which reversed the lower categories on the page.)
  // Centring the group on the bone stops a short bone bunching its causes
  // against the category label.
  let cursor = Math.min(yTip, ySpine) + (sideRun - bone.run) / 2 + RUN_PAD;

  return (
    <Fragment>
      {bone.causes.map((c, i) => {
        const top = cursor;
        const tickY = top + c.height / 2;
        cursor = top + c.height + BLOCK_GAP;

        // Interpolate along the bone rather than assuming a straight column:
        // the tick has to touch the diagonal wherever this block sits.
        const t = Math.abs(tickY - yTip) / sideRun;
        const tickX = bone.xTip + t * (bone.xJoin - bone.xTip);
        const textX = tickX - TICK_LEN - TEXT_GAP;

        return (
          <Fragment key={`${bone.category}-${i}`}>
            <line
              x1={tickX}
              y1={tickY}
              x2={tickX - TICK_LEN}
              y2={tickY}
              stroke={bone.colour}
              stroke-width={1.75}
            />
            {c.causeLines.map((line, li) => (
              <text
                key={`c${li}`}
                x={textX}
                y={top + CAUSE_LINE * li + CAUSE_FONT}
                text-anchor="end"
                font-size={CAUSE_FONT}
                font-weight="700"
                fill="var(--gc-foreground, #111827)"
              >
                {line}
              </text>
            ))}
            {c.explLines.map((line, li) => (
              <text
                key={`e${li}`}
                x={textX}
                y={top + c.causeLines.length * CAUSE_LINE + EXPL_LINE * li + EXPL_FONT}
                text-anchor="end"
                font-size={EXPL_FONT}
                fill="var(--gc-text-muted, #6b7280)"
              >
                {line}
              </text>
            ))}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

/** Stacked, colour-coded fallback used when the container is too narrow. */
function IshikawaList({ bones }: { bones: IshikawaBone[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {bones.map((bone) => (
        <div key={bone.category} style={{ borderLeft: `3px solid ${bone.colour}`, paddingLeft: 12 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: bone.colour,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 6,
            }}
          >
            {bone.category}
          </div>
          {bone.causes.map((c, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: 'var(--gc-foreground, inherit)' }}>
                {c.cause}
              </div>
              {c.explanation && (
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--gc-text-muted, #6b7280)' }}>
                  {c.explanation}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Client-rendered replacement for the pre-generated Ishikawa PNGs: draws the
 * fishbone straight from the stored RCA JSON.
 *
 * The old AI_Incident_Tracker pipeline built this diagram as an SVG string in
 * Python (ishikawa.py), rasterised it with cairosvg and pushed the PNG to
 * Airtable, because Airtable renders SVG attachments poorly. Nothing here
 * needs a raster step — the categories, colours, spine-and-bones geometry and
 * right-aligned cause blocks are kept, but laid out at the container's real
 * pixel width so text is always drawn at its true size instead of being
 * scaled down from a fixed 1300px canvas. Two things the Python version could
 * not do follow from that: the canvas grows to fit long explanations rather
 * than letting them overlap, and text is escaped by the renderer (the raw '&'
 * in "Sky Betting & Gaming" broke the Python XML until it was patched).
 */
export function IshikawaDiagram({ bones, title = '' }: IshikawaDiagramProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!hostRef.current) return;
    const ro = createResizeObserver(hostRef.current, (w) => setWidth(Math.round(w)));
    return () => ro.disconnect();
  }, []);

  if (bones.length === 0) return null;

  let content: h.JSX.Element | null = null;

  if (width > 0 && width < FISHBONE_MIN_WIDTH) {
    content = <IshikawaList bones={bones} />;
  } else if (width > 0) {
    const headWidth = Math.min(220, Math.max(140, Math.round(width * 0.2)));
    const spineLen = width - PAD_X * 2 - headWidth - 10;

    const top = layoutSide(bones.filter((b) => b.top), spineLen);
    const bottom = layoutSide(bones.filter((b) => !b.top), spineLen);

    const topLabelBand = top.bones.length > 0 ? LABEL_H + LABEL_GAP : 0;
    const bottomLabelBand = bottom.bones.length > 0 ? LABEL_H + LABEL_GAP : 0;

    const titleLines = wrap(title, charBudget(headWidth - 16, 12, 0.56));
    const headHeight = Math.max(LABEL_H, titleLines.length * 15 + 14);

    // The head box is centred on the spine, so the spine has to clear half of
    // it — otherwise a record with no categories above the spine draws the
    // head off the top of the canvas.
    const ySpine = Math.max(PAD_Y + topLabelBand + top.run, PAD_Y + headHeight / 2);
    const height = Math.max(
      ySpine + bottom.run + bottomLabelBand + PAD_Y,
      ySpine + headHeight / 2 + PAD_Y
    );
    const yTopTip = ySpine - top.run;
    const yBottomTip = ySpine + bottom.run;
    const xSpineEnd = PAD_X + spineLen;

    const label = (bone: LaidOutBone, isTop: boolean) => {
      const boxY = isTop ? yTopTip - LABEL_GAP - LABEL_H : yBottomTip + LABEL_GAP;
      return (
        <Fragment key={`label-${bone.category}`}>
          <rect
            x={bone.xTip - bone.labelWidth / 2}
            y={boxY}
            width={bone.labelWidth}
            height={LABEL_H}
            rx={4}
            fill="none"
            stroke={bone.colour}
            stroke-width={1.5}
          />
          <text
            x={bone.xTip}
            y={boxY + LABEL_H / 2 + bone.labelFont * 0.36}
            text-anchor="middle"
            font-size={bone.labelFont}
            font-weight="700"
            fill={bone.colour}
          >
            {bone.category}
          </text>
        </Fragment>
      );
    };

    content = (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Fishbone diagram: potential causes of the incident grouped into ${bones.length} categories — ${bones.map((b) => b.category).join(', ')}`}
        style={{ maxWidth: '100%', fontFamily: 'inherit', display: 'block' }}
      >
        {/* Spine, with an arrowhead running into the incident (the effect). */}
        <line
          x1={PAD_X}
          y1={ySpine}
          x2={xSpineEnd}
          y2={ySpine}
          stroke="var(--gc-foreground, #111827)"
          stroke-width={SPINE_W}
        />
        <polygon
          points={`${xSpineEnd},${ySpine - 6} ${xSpineEnd + 9},${ySpine} ${xSpineEnd},${ySpine + 6}`}
          fill="var(--gc-foreground, #111827)"
        />

        {titleLines.length > 0 && (
          <Fragment>
            <rect
              x={xSpineEnd + 11}
              y={ySpine - headHeight / 2}
              width={headWidth - 11}
              height={headHeight}
              rx={5}
              fill="none"
              stroke="var(--gc-border, #d1d5db)"
              stroke-width={1.5}
            />
            {titleLines.map((line, i) => (
              <text
                key={i}
                x={xSpineEnd + 11 + (headWidth - 11) / 2}
                y={ySpine - (titleLines.length * 15) / 2 + 15 * i + 12}
                text-anchor="middle"
                font-size={12}
                font-weight="700"
                fill="var(--gc-foreground, #111827)"
              >
                {line}
              </text>
            ))}
          </Fragment>
        )}

        {top.bones.map((bone) => (
          <Fragment key={bone.category}>
            <line
              x1={bone.xTip}
              y1={yTopTip}
              x2={bone.xJoin}
              y2={ySpine}
              stroke={bone.colour}
              stroke-width={SPINE_W}
            />
            {label(bone, true)}
            <BoneCauses bone={bone} sideRun={top.run} yTip={yTopTip} ySpine={ySpine} />
          </Fragment>
        ))}

        {bottom.bones.map((bone) => (
          <Fragment key={bone.category}>
            <line
              x1={bone.xTip}
              y1={yBottomTip}
              x2={bone.xJoin}
              y2={ySpine}
              stroke={bone.colour}
              stroke-width={SPINE_W}
            />
            {label(bone, false)}
            <BoneCauses bone={bone} sideRun={bottom.run} yTip={yBottomTip} ySpine={ySpine} />
          </Fragment>
        ))}
      </svg>
    );
  }

  return (
    <div ref={hostRef} style={{ width: '100%' }}>
      {content}
    </div>
  );
}
